import React from 'react';
import {
  List,
  ListRowProps,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import { IListboxComponentProps } from './types';

const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  IListboxComponentProps
>(function ListboxComponent({ children, role, ...other }, ref) {
  const {
    scrollRef = undefined,
    scrollMode = 'POSITION',
    ...restOther
  } = other;

  const listRef = React.useRef<List | null>(null);
  const anchorScroll = React.useRef<number>(0);
  const itemCount = React.useMemo(
    () => (Array.isArray(children) ? children.length : 0),
    [children],
  );

  const cache = React.useMemo(() => {
    const newCache = new CellMeasurerCache({
      defaultWidth: 100,
      minWidth: 75,
      defaultHeight: 50,
      fixedHeight: true,
    });

    return newCache;
  }, []);

  const height = React.useMemo(
    () => Math.min(200, itemCount * cache.defaultHeight),
    [cache.defaultHeight, itemCount],
  );

  const onRowRender = React.useCallback(
    (props: ListRowProps) => {
      const { index, key, parent, style } = props;
      if (children) {
        return (
          <CellMeasurer
            cache={cache}
            columnIndex={0}
            key={key}
            parent={parent}
            rowIndex={index}
          >
            {({ registerChild }) => {
              return React.cloneElement(children[props.index], {
                ref: registerChild,
                style,
              });
            }}
          </CellMeasurer>
        );
      }

      return undefined;
    },
    [cache, children],
  );

  const onScroll = React.useCallback(
    ({ scrollTop }: { scrollTop: number }) => {
      if (scrollTop) {
        anchorScroll.current = scrollTop;
      }
    },
    [anchorScroll],
  );

  React.useEffect(() => {
    if (listRef.current && scrollRef && scrollRef.current) {
      if (scrollMode === 'POSITION')
        listRef.current.scrollToPosition(scrollRef.current);
      else {
        listRef.current.scrollToRow(scrollRef.current);
      }
    }

    return () => {
      if (scrollRef && scrollMode === 'POSITION') {
        // eslint-disable-next-line no-param-reassign
        scrollRef.current = anchorScroll.current;
      }
    };
  }, [scrollRef, scrollMode]);

  return (
    <div ref={ref}>
      <div {...restOther}>
        <List
          onScroll={onScroll}
          deferredMeasurementCache={cache}
          rowHeight={cache.rowHeight}
          ref={listRef}
          height={height}
          width={1200}
          overscanCount={5}
          rowCount={itemCount}
          rowRenderer={onRowRender}
          role={role}
        />
      </div>
    </div>
  );
});

export default ListboxComponent;
